/**
 * EEOC Training Dashboard
 * Main dashboard for EEOC Compliance Training module
 */
import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';

import {
  Shield,
  Plus,
  Search,
  Copy,
  Trash2,
  ExternalLink,
  RefreshCw,
  Users,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Award,
  BarChart3,
  BookOpen,
  MessageSquare,
  MoreVertical,
  Play,
  Target,
  TrendingUp,
  Loader2,
  User,
  Mail,
  Eye,
  GraduationCap,
  ChevronRight,
  FileText,
  Trophy,
  Clock,
  Lightbulb,
  ListChecks,
  HelpCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';

// EEOC Reference Library Content - Same as UserEEOC for consistency
const LIBRARY_SECTIONS = [
  {
    id: 'overview',
    title: 'EEOC Overview',
    icon: Shield,
    description: 'Understanding Equal Employment Opportunity laws',
    content: [
      'The EEOC enforces federal laws prohibiting employment discrimination',
      'Protected characteristics include race, color, religion, sex, national origin, age (40+), disability, and genetic information',
      'Discrimination is prohibited in hiring, firing, pay, promotions, training, and all employment decisions',
      'Violations can result in lawsuits, fines up to $300,000 per violation, and reputational damage',
      'Both intentional discrimination and policies with discriminatory effects are illegal'
    ]
  },
  {
    id: 'protected',
    title: 'Protected Categories',
    icon: Users,
    description: '8 federally protected characteristics',
    content: [
      'Age (ADEA) - Protects individuals 40 years or older from age-based discrimination',
      'Race & Color (Title VII) - Prohibits discrimination based on race, skin color, or ethnicity',
      'National Origin - Protects against discrimination based on birthplace, ancestry, culture, or accent',
      'Religion - Covers beliefs, practices, and requires reasonable accommodation',
      'Sex/Gender - Includes pregnancy, sexual orientation, and gender identity (Bostock v. Clayton)',
      'Disability (ADA) - Protects qualified individuals; requires reasonable accommodations',
      'Genetic Information (GINA) - Prohibits using family medical history in employment decisions',
      'Pregnancy (PDA) - Cannot discriminate based on pregnancy, childbirth, or related conditions'
    ]
  },
  {
    id: 'interview',
    title: 'Interview Do\'s & Don\'ts',
    icon: ListChecks,
    description: 'What you can and cannot ask',
    content: [
      'DO: Ask about ability to perform specific job duties with or without accommodation',
      'DO: Ask about availability for the work schedule required',
      'DO: Ask about legal authorization to work in the US',
      'DO: Ask behavioral questions about past work performance',
      'DON\'T: Ask about age, birth date, or when they graduated',
      'DON\'T: Ask about marital status, children, or pregnancy plans',
      'DON\'T: Ask about religious practices, holidays observed, or church attendance',
      'DON\'T: Ask about disabilities, medical history, or workers\' compensation claims',
      'DON\'T: Ask where they were born or about citizenship (only work authorization)'
    ]
  },
  {
    id: 'scenarios',
    title: 'Common Scenarios',
    icon: Lightbulb,
    description: 'How to handle tricky situations professionally',
    content: [
      'Candidate mentions pregnancy → "Thank you for sharing. Let me focus on the job requirements..."',
      'Candidate reveals disability → "Our focus is on whether you can perform essential functions..."',
      'Candidate mentions religious practices → "We discuss accommodations after an offer is made..."',
      'Candidate shares age/graduation year → "Let\'s discuss your recent relevant experience..."',
      'Candidate has accent → Never comment on accents; focus only on communication skills needed for role',
      'Candidate mentions caregiving → Don\'t ask follow-ups; stick to schedule availability questions'
    ]
  },
  {
    id: 'redirection',
    title: 'Redirection Techniques',
    icon: Target,
    description: 'Professional ways to stay compliant',
    content: [
      '"That\'s interesting. Let me ask about your experience with [specific job requirement]..."',
      '"I appreciate you sharing. Regarding this role, can you tell me about..."',
      '"We can discuss any accommodations after an offer. For now, let\'s focus on..."',
      '"Let me redirect to your qualifications. What experience do you have with..."',
      'Acknowledge briefly → Pause → Pivot to job-relevant topic',
      'Never follow up on protected information; always redirect immediately'
    ]
  },
  {
    id: 'consequences',
    title: 'Legal Consequences',
    icon: AlertTriangle,
    description: 'Why compliance matters',
    content: [
      'Individual liability: Interviewers can be personally named in lawsuits',
      'Company fines: Up to $300,000 per violation for large employers',
      'Back pay and front pay damages for affected candidates',
      'Punitive damages for intentional discrimination',
      'Attorney fees and court costs',
      'Mandatory policy changes and compliance monitoring',
      'Reputational damage affecting recruiting and business relationships'
    ]
  }
];

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subtitle, className }) => (
  <Card className={cn("bg-card border-border", className)}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className={cn("text-2xl font-bold", color || "text-foreground")}>{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={cn("p-3 rounded-lg", `bg-${color?.replace('text-', '')}/10` || "bg-muted")}>
          <Icon className={cn("h-6 w-6", color || "text-muted-foreground")} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Status Badge Component for My Training tab
const StatusBadge = ({ status }) => {
  const statusConfig = {
    qualified: { icon: CheckCircle, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Qualified' },
    needs_improvement: { icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Needs Improvement' },
    not_qualified: { icon: XCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Not Qualified' },
    not_started: { icon: Clock, color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400', label: 'Not Started' },
    in_progress: { icon: Play, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'In Progress' },
    completed: { icon: CheckCircle, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Completed' },
  };
  
  const config = statusConfig[status] || statusConfig.not_started;
  const Icon = config.icon;
  
  return (
    <Badge className={cn("gap-1", config.color)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export default function EEOCDashboard() {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  const context = useOutletContext();
  const theme = context?.theme || 'light';
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [tests, setTests] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [users, setUsers] = useState([]);
  const [teamCompliance, setTeamCompliance] = useState([]);
  const [complianceSummary, setComplianceSummary] = useState({});
  const [eeocSettings, setEeocSettings] = useState({});
  const [departmentStats, setDepartmentStats] = useState([]);
  
  // My Training state - for admins/auditors to take training themselves
  const [myTests, setMyTests] = useState([]);
  const [myTrainingTab, setMyTrainingTab] = useState('overview');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [qualificationFilter, setQualificationFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creating, setCreating] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // External user form state
  const [externalName, setExternalName] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  
  const [selectedTest, setSelectedTest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Check if current user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.is_admin;

  useEffect(() => {
    if (tenant?.id) {
      fetchData();
    }
  }, [tenant?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const tenantId = tenant?.id;
      
      // Fetch team compliance data (matches Admin Users page)
      const complianceRes = await api.get(`/eeoc/admin/team-compliance?tenant_id=${tenantId}`);
      const complianceData = complianceRes.data.users || [];
      setTeamCompliance(complianceData);
      setComplianceSummary(complianceRes.data.summary || {});
      setStats(complianceRes.data.summary || {});
      
      // Calculate department stats
      const deptMap = {};
      complianceData.forEach(user => {
        const dept = typeof user.department === 'object' ? user.department?.name : user.department || 'Unassigned';
        if (!deptMap[dept]) {
          deptMap[dept] = { total: 0, completed: 0, qualified: 0, avgScore: [], name: dept };
        }
        deptMap[dept].total++;
        if (user.eeoc_status.test_status === 'completed') deptMap[dept].completed++;
        if (user.eeoc_status.qualification_status === 'qualified') deptMap[dept].qualified++;
        if (user.eeoc_status.score !== null) deptMap[dept].avgScore.push(user.eeoc_status.score);
      });
      const deptStats = Object.values(deptMap).map(d => ({
        ...d,
        avgScore: d.avgScore.length > 0 ? Math.round(d.avgScore.reduce((a, b) => a + b, 0) / d.avgScore.length) : null,
        completionRate: d.total > 0 ? Math.round((d.completed / d.total) * 100) : 0
      }));
      setDepartmentStats(deptStats);
      
      // Fetch EEOC settings
      try {
        const settingsRes = await api.get(`/eeoc/admin/eeoc-settings?tenant_id=${tenantId}`);
        setEeocSettings(settingsRes.data || {});
      } catch (e) {
        console.log('EEOC settings not available');
      }
      
      // Fetch external tests only
      const testsRes = await api.get(`/eeoc/admin/tests?tenant_id=${tenantId}`);
      setTests(testsRes.data.filter(t => t.source === 'external') || []);
      
      // Fetch scenarios
      const scenariosRes = await api.get(`/eeoc/scenarios?tenant_id=${tenantId}`);
      setScenarios(scenariosRes.data);
      
      // Fetch users for test creation
      try {
        const usersRes = await api.get(`/eeoc/admin/assignable-users?tenant_id=${tenantId}`);
        setUsers(usersRes.data.users || []);
      } catch (e) {
        console.error('Failed to fetch users:', e);
      }
      
      // Fetch current user's own EEOC tests (for My Training tab)
      if (user?.id) {
        try {
          const myTestsRes = await api.get(`/eeoc/my-tests?user_id=${user.id}&tenant_id=${tenantId}`);
          setMyTests(myTestsRes.data.tests || []);
        } catch (e) {
          console.log('Could not fetch user EEOC tests');
          setMyTests([]);
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch EEOC data:', error);
      if (error.response?.status === 403) {
        toast.error('EEOC Training is not enabled for your organization');
      } else {
        toast.error('Failed to load EEOC data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post(`/eeoc/admin/eeoc-settings?tenant_id=${tenant?.id}&eeoc_mandatory=${eeocSettings.eeoc_mandatory || false}&eeoc_deadline_days=${eeocSettings.eeoc_deadline_days || 30}`);
      toast.success('EEOC settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCreateTest = async () => {
    if (!externalName.trim() || !externalEmail.trim()) {
      toast.error('Please enter name and email');
      return;
    }
    
    // Basic email validation
    if (!externalEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setCreating(true);
    try {
      // Create test for external user
      const res = await api.post(`/eeoc/admin/tests?tenant_id=${tenant.id}`, {
        tenant_user_id: `external-${Date.now()}`,
        user_name: externalName.trim(),
        user_email: externalEmail.trim()
      });
      
      // Mark as external source
      const testWithSource = { ...res.data, source: 'external' };
      setTests([testWithSource, ...tests]);
      setCreateDialogOpen(false);
      setExternalName('');
      setExternalEmail('');
      
      // Copy link to clipboard
      const link = `${window.location.origin}/eeoc/test/${res.data.link_id}`;
      await navigator.clipboard.writeText(link);
      toast.success('Test link created and copied to clipboard!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create test');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async (test) => {
    const link = `${window.location.origin}/eeoc/test/${test.link_id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied to clipboard');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    
    try {
      await api.delete(`/eeoc/admin/tests/${testId}?tenant_id=${tenant.id}`);
      setTests(tests.filter(t => t.id !== testId));
      toast.success('Test deleted');
    } catch (error) {
      toast.error('Failed to delete test');
    }
  };

  const handleViewTest = async (test) => {
    setSelectedTest(test);
    setDetailDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_started: 'bg-muted text-muted-foreground',
      in_progress: 'bg-blue-500/20 text-blue-500',
      completed: 'bg-slate-500/20 text-slate-500',
    };
    const labels = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      completed: 'Completed',
    };
    return <Badge variant="outline" className={cn(styles[status], "font-mono text-xs")}>{labels[status]}</Badge>;
  };

  const getQualificationBadge = (status) => {
    if (!status) return <span className="text-muted-foreground text-sm">-</span>;
    
    const styles = {
      qualified: 'bg-emerald-500/20 text-emerald-500',
      needs_improvement: 'bg-amber-500/20 text-amber-500',
      not_qualified: 'bg-red-500/20 text-red-500',
    };
    const labels = {
      qualified: 'Qualified',
      needs_improvement: 'Needs Training',
      not_qualified: 'Not Qualified',
    };
    return <Badge variant="outline" className={cn(styles[status], "font-mono text-xs")}>{labels[status]}</Badge>;
  };

  // Get unique managers for filter
  const managers = [...new Set(teamCompliance.filter(u => u.manager).map(u => u.manager.id))].map(id => {
    const userWithManager = teamCompliance.find(u => u.manager?.id === id);
    return { id, name: userWithManager?.manager?.name || 'Unknown' };
  });

  // Get unique departments for filter
  const departments = [...new Set(teamCompliance.map(u => typeof u.department === 'object' ? u.department?.name : u.department).filter(Boolean))];

  // Filter team compliance data
  const filteredCompliance = teamCompliance.filter(user => {
    if (statusFilter !== 'all' && user.eeoc_status.test_status !== statusFilter) return false;
    if (qualificationFilter !== 'all' && user.eeoc_status.qualification_status !== qualificationFilter) return false;
    if (managerFilter !== 'all' && user.reports_to !== managerFilter) return false;
    if (departmentFilter !== 'all') {
      const userDept = typeof user.department === 'object' ? user.department?.name : user.department;
      if (userDept !== departmentFilter) return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return user.name?.toLowerCase().includes(query) || user.email?.toLowerCase().includes(query);
    }
    return true;
  });

  // Filter external tests
  const filteredTests = tests.filter(test => {
    if (statusFilter !== 'all' && test.test_status !== statusFilter) return false;
    if (qualificationFilter !== 'all' && test.qualification_status !== qualificationFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return test.user_name?.toLowerCase().includes(query) || test.user_email?.toLowerCase().includes(query);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="eeoc-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Shield className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">EEOC Compliance Training</h1>
            <p className="text-sm text-muted-foreground">Monitor team compliance and manage external assessments</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard title="Total Users" value={complianceSummary.total_users || 0} icon={Users} color="text-blue-500" />
        <StatCard title="Completed" value={complianceSummary.completed || 0} icon={CheckCircle} color="text-slate-500" />
        <StatCard title="Qualified" value={complianceSummary.qualified || 0} icon={Award} color="text-emerald-500" />
        <StatCard title="Needs Training" value={complianceSummary.needs_improvement || 0} icon={AlertTriangle} color="text-amber-500" />
        <StatCard title="Not Started" value={complianceSummary.not_started || 0} icon={Target} color="text-gray-500" />
        <StatCard title="Compliance Rate" value={`${complianceSummary.compliance_rate || 0}%`} icon={BarChart3} color="text-purple-500" />
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="my-training" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="my-training" className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            My Training
          </TabsTrigger>
          <TabsTrigger value="compliance" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Team Compliance
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="external" className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4" />
            External Assessments
          </TabsTrigger>
          <TabsTrigger value="scenarios" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Scenario Library
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Settings
            </TabsTrigger>
          )}
        </TabsList>

        {/* My Training Tab - For admins/auditors to take training */}
        <TabsContent value="my-training">
          <div className="space-y-6">
            {/* My Current Status */}
            {(() => {
              const latestCompletedTest = myTests.find(t => t.test_status === 'completed');
              const hasCompletedAssessment = !!latestCompletedTest;
              const currentScore = latestCompletedTest?.score;
              const qualificationStatus = latestCompletedTest?.qualification_status;
              
              return (
                <>
                  {/* Status Banner */}
                  <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-white/20 rounded-lg">
                            <Shield className="h-8 w-8" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">Your EEOC Training Status</h2>
                            <p className="text-purple-100">Complete training to stay compliant with EEOC regulations</p>
                          </div>
                        </div>
                        {hasCompletedAssessment && (
                          <div className="text-right">
                            <div className="text-sm text-purple-200">Your Score</div>
                            <div className="text-3xl font-bold">{currentScore}%</div>
                            <StatusBadge status={qualificationStatus} />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Scenario Book */}
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-indigo-200" 
                          onClick={() => navigate('/dashboard/eeoc/scenarios')}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                            <BookOpen className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">Scenario Book</h3>
                            <p className="text-sm text-muted-foreground">Study before testing</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="w-full justify-center mt-3 text-indigo-600 border-indigo-300">
                          Recommended First Step
                        </Badge>
                        <Button className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700 text-white">
                          Study Scenarios
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Mock Test */}
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-200" 
                          onClick={() => navigate('/dashboard/eeoc/mock')}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Play className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">Practice Test</h3>
                            <p className="text-sm text-muted-foreground">Practice without scoring</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="w-full justify-center mt-3 text-blue-600 border-blue-300">
                          2 Practice Scenarios
                        </Badge>
                        <Button className="w-full mt-3" variant="outline">
                          Start Practice
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Assessment */}
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-green-200" 
                          onClick={() => navigate('/dashboard/eeoc/assessment')}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                            <Award className="h-6 w-6 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">Assessment</h3>
                            <p className="text-sm text-muted-foreground">Official evaluation</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="w-full justify-center mt-3 text-green-600 border-green-300">
                          5 Scored Scenarios
                        </Badge>
                        <Button className="w-full mt-3 bg-green-600 hover:bg-green-700">
                          Take Assessment
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Library */}
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-amber-200" 
                          onClick={() => setMyTrainingTab('library')}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                            <FileText className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">Reference Library</h3>
                            <p className="text-sm text-muted-foreground">Guidelines & tips</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="w-full justify-center mt-3 text-amber-600 border-amber-300">
                          6 Topics Covered
                        </Badge>
                        <Button className="w-full mt-3" variant="outline">
                          Browse Library
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sub-tabs for My Training */}
                  <Tabs value={myTrainingTab} onValueChange={setMyTrainingTab} className="space-y-4">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="overview" className="gap-2">
                        <Target className="h-4 w-4" />
                        Overview
                      </TabsTrigger>
                      <TabsTrigger value="history" className="gap-2">
                        <Trophy className="h-4 w-4" />
                        My Results
                      </TabsTrigger>
                      <TabsTrigger value="library" className="gap-2">
                        <BookOpen className="h-4 w-4" />
                        Library
                      </TabsTrigger>
                    </TabsList>

                    {/* Overview */}
                    <TabsContent value="overview">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <GraduationCap className="h-5 w-5" />
                            About EEOC Training
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-muted-foreground">
                            This training helps you conduct interviews that comply with Equal Employment Opportunity Commission (EEOC) guidelines. 
                            You&apos;ll practice handling scenarios where candidates may disclose protected information.
                          </p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                              <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Practice Test</h4>
                              <ul className="text-sm text-blue-600 dark:text-blue-300 space-y-1">
                                <li>• 2 practice scenarios</li>
                                <li>• No score recorded</li>
                                <li>• Unlimited attempts</li>
                                <li>• Learn from mistakes</li>
                              </ul>
                            </div>
                            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                              <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">Assessment</h4>
                              <ul className="text-sm text-green-600 dark:text-green-300 space-y-1">
                                <li>• 5 evaluation scenarios</li>
                                <li>• Score recorded</li>
                                <li>• Detailed feedback</li>
                                <li>• Qualification status</li>
                              </ul>
                            </div>
                          </div>

                          <Separator className="my-4" />

                          <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                            <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                            <div>
                              <h4 className="font-semibold text-amber-700 dark:text-amber-400">Scoring Criteria</h4>
                              <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
                                You&apos;ll be evaluated on safe questioning, boundary awareness, neutral language, and proper redirection techniques. 
                                Score 80+ to be qualified, 60-79 needs improvement, below 60 requires retraining.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* My Results */}
                    <TabsContent value="history">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Trophy className="h-5 w-5" />
                            My Assessment History
                          </CardTitle>
                          <CardDescription>
                            View your past assessments and track your progress
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {myTests.length === 0 ? (
                            <div className="text-center py-8">
                              <Award className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                              <h3 className="font-medium text-lg">No Assessments Yet</h3>
                              <p className="text-muted-foreground mt-1">
                                Take your first assessment to see your results here
                              </p>
                              <Button className="mt-4" onClick={() => navigate('/dashboard/eeoc/assessment')}>
                                Start Assessment
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {myTests.map((test) => (
                                <div 
                                  key={test.id}
                                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                                >
                                  <div className="flex items-center gap-4">
                                    <div className={cn(
                                      "p-2 rounded-lg",
                                      test.test_status === 'completed' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'
                                    )}>
                                      {test.test_status === 'completed' ? (
                                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                                      ) : (
                                        <Clock className="h-5 w-5 text-gray-500" />
                                      )}
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">
                                          {test.is_mock ? 'Practice Test' : 'Assessment'}
                                        </span>
                                        <StatusBadge status={test.test_status} />
                                      </div>
                                      <p className="text-sm text-muted-foreground">
                                        {new Date(test.created_at).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-4">
                                    {test.score != null && (
                                      <div className="text-right">
                                        <div className="text-2xl font-bold">{test.score}%</div>
                                        <StatusBadge status={test.qualification_status} />
                                      </div>
                                    )}
                                    {test.test_status === 'completed' && test.link_id && (
                                      <Button variant="outline" size="sm" onClick={() => navigate(`/eeoc/test/${test.link_id}/results`)}>
                                        View Details
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Library */}
                    <TabsContent value="library">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <BookOpen className="h-5 w-5" />
                            EEOC Reference Library
                          </CardTitle>
                          <CardDescription>
                            Essential guidelines and best practices for compliant interviewing
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {LIBRARY_SECTIONS.map((section) => {
                              const SectionIcon = section.icon;
                              return (
                                <Card key={section.id} className="border">
                                  <CardHeader className="pb-2">
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                        <SectionIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                      </div>
                                      <div>
                                        <CardTitle className="text-base">{section.title}</CardTitle>
                                        <CardDescription className="text-xs">{section.description}</CardDescription>
                                      </div>
                                    </div>
                                  </CardHeader>
                                  <CardContent>
                                    <ul className="space-y-2">
                                      {section.content.map((item, idx) => (
                                        <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                          <ChevronRight className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                                          <span>{item}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </>
              );
            })()}
          </div>
        </TabsContent>

        {/* Analytics Tab - Department Breakdown */}
        <TabsContent value="analytics">
          <div className="space-y-6">
            {/* Department Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-purple-500" />
                  Department Breakdown
                </CardTitle>
                <CardDescription>
                  EEOC training completion and scores by department
                </CardDescription>
              </CardHeader>
              <CardContent>
                {departmentStats.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No department data available</p>
                ) : (
                  <div className="space-y-4">
                    {departmentStats.map((dept, i) => (
                      <div key={i} className="p-4 border rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-foreground">{dept.name}</h4>
                            <p className="text-sm text-muted-foreground">{dept.total} team members</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-purple-600">{dept.completionRate}%</p>
                            <p className="text-xs text-muted-foreground">Completion Rate</p>
                          </div>
                        </div>
                        <Progress value={dept.completionRate} className="h-2" />
                        <div className="grid grid-cols-3 gap-4 text-center pt-2">
                          <div>
                            <p className="text-lg font-semibold text-emerald-600">{dept.qualified}</p>
                            <p className="text-xs text-muted-foreground">Qualified</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-blue-600">{dept.completed}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-amber-600">{dept.avgScore !== null ? `${dept.avgScore}%` : '-'}</p>
                            <p className="text-xs text-muted-foreground">Avg Score</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Role Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Role Distribution
                </CardTitle>
                <CardDescription>
                  EEOC training status by role (includes admins and auditors)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['admin', 'owner', 'quality_auditor', 'qa_lead', 'recruiter', 'user'].map(role => {
                    const roleUsers = teamCompliance.filter(u => u.role === role);
                    const completed = roleUsers.filter(u => u.eeoc_status.test_status === 'completed').length;
                    const qualified = roleUsers.filter(u => u.eeoc_status.qualification_status === 'qualified').length;
                    if (roleUsers.length === 0) return null;
                    return (
                      <div key={role} className="p-4 bg-muted/50 rounded-lg">
                        <Badge variant="outline" className="mb-2 capitalize">{role.replace('_', ' ')}</Badge>
                        <p className="text-2xl font-bold">{roleUsers.length}</p>
                        <p className="text-xs text-muted-foreground">
                          {completed} completed • {qualified} qualified
                        </p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Settings Tab - EEOC Configuration */}
        {isAdmin && (
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-indigo-500" />
                  EEOC Training Settings
                </CardTitle>
                <CardDescription>
                  Configure EEOC training requirements for your organization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Mandatory Training</Label>
                    <p className="text-sm text-muted-foreground">
                      Require all users to complete EEOC training
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={eeocSettings.eeoc_mandatory ? "default" : "secondary"}>
                      {eeocSettings.eeoc_mandatory ? "Enabled" : "Disabled"}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEeocSettings({...eeocSettings, eeoc_mandatory: !eeocSettings.eeoc_mandatory})}
                    >
                      {eeocSettings.eeoc_mandatory ? "Disable" : "Enable"}
                    </Button>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg space-y-3">
                  <Label className="text-base font-medium">Training Deadline (Days)</Label>
                  <p className="text-sm text-muted-foreground">
                    Number of days new users have to complete EEOC training
                  </p>
                  <Select 
                    value={String(eeocSettings.eeoc_deadline_days || 30)}
                    onValueChange={(v) => setEeocSettings({...eeocSettings, eeoc_deadline_days: parseInt(v)})}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-indigo-600 hover:bg-indigo-700">
                    {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Team Compliance Tab - Internal Users */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-2">
                <CardTitle className="text-lg">Team Compliance Tracking</CardTitle>
                <CardDescription>
                  Track EEOC training completion for your team members. This matches users from Admin → Users page.
                </CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-48"
                    data-testid="search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={qualificationFilter} onValueChange={setQualificationFilter}>
                  <SelectTrigger className="w-40" data-testid="qualification-filter">
                    <SelectValue placeholder="Qualification" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Results</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="needs_improvement">Needs Training</SelectItem>
                    <SelectItem value="not_qualified">Not Qualified</SelectItem>
                  </SelectContent>
                </Select>
                {managers.length > 0 && (
                  <Select value={managerFilter} onValueChange={setManagerFilter}>
                    <SelectTrigger className="w-44" data-testid="manager-filter">
                      <SelectValue placeholder="Filter by Manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Managers</SelectItem>
                      {managers.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {departments.length > 0 && (
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="w-44" data-testid="department-filter">
                      <SelectValue placeholder="Filter by Department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role / Department</TableHead>
                    <TableHead>Reports To</TableHead>
                    <TableHead>EEOC Status</TableHead>
                    <TableHead>Qualification</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompliance.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        {teamCompliance.length === 0 
                          ? 'No team members found. Add users in Admin → Users page first.'
                          : 'No users match your filters.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCompliance.map((user) => (
                      <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-foreground">{user.name}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {user.role && (
                              <Badge variant="outline" className="text-xs capitalize">{user.role}</Badge>
                            )}
                            {user.department && (
                              <p className="text-xs text-muted-foreground">
                                {typeof user.department === 'object' ? user.department.name : user.department}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.manager ? (
                            <span className="text-sm text-muted-foreground">{user.manager.name}</span>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Top Level</Badge>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(user.eeoc_status.test_status)}</TableCell>
                        <TableCell>{getQualificationBadge(user.eeoc_status.qualification_status)}</TableCell>
                        <TableCell className="text-center font-mono">
                          {user.eeoc_status.score !== null ? `${user.eeoc_status.score}%` : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {user.eeoc_status.test_status === 'completed' && user.eeoc_status.link_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`/eeoc/test/${user.eeoc_status.link_id}/results`, '_blank')}
                                className="h-8 w-8 p-0"
                                title="View results"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/dashboard/user-session?userId=${user.user_id}`)}
                              className="h-8 w-8 p-0"
                              title="View user session"
                            >
                              <User className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* External Assessments Tab */}
        <TabsContent value="external">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div>
                  <CardTitle className="text-lg">External Assessments</CardTitle>
                  <CardDescription>
                    Create and manage test links for external candidates or contractors who are not CallSense users.
                  </CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={() => setCreateDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Create External Link
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Qualification</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTests.filter(t => t.source === 'external').length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        {tests.filter(t => t.source === 'external').length === 0 
                          ? 'No external test links created yet. Click "Create External Link" to get started.'
                          : 'No tests match your filters.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTests.filter(t => t.source === 'external').map((test) => (
                      <TableRow key={test.id} data-testid={`external-test-row-${test.id}`}>
                        <TableCell>
                          <p className="font-medium text-foreground">{test.user_name}</p>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{test.user_email}</TableCell>
                        <TableCell>{getStatusBadge(test.test_status)}</TableCell>
                        <TableCell>{getQualificationBadge(test.qualification_status)}</TableCell>
                        <TableCell className="text-center font-mono">
                          {test.score !== null ? `${test.score}%` : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(test.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyLink(test)}
                              className="h-8 w-8 p-0"
                              title="Copy test link"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/eeoc/test/${test.link_id}`, '_blank')}
                              className="h-8 w-8 p-0"
                              title="Open test"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            {test.test_status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewTest(test)}
                                className="h-8 w-8 p-0"
                                title="View results"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTest(test.id)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                                title="Delete test"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scenarios Tab */}
        <TabsContent value="scenarios">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Scenario Library</CardTitle>
                  <CardDescription>Real scenarios submitted through the Scenario Helper</CardDescription>
                </div>
                <Badge variant="outline">{scenarios.length} scenarios</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {scenarios.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No scenarios submitted yet.</p>
                  <p className="text-sm">Scenarios will appear here when users ask for guidance.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {scenarios.map((scenario) => (
                      <Card key={scenario.id} className="bg-muted/30">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {scenario.category && (
                                  <Badge variant="outline" className="text-xs">
                                    {scenario.category}
                                  </Badge>
                                )}
                                {scenario.is_case_study && (
                                  <Badge className="bg-blue-500/20 text-blue-500 text-xs">Case Study</Badge>
                                )}
                              </div>
                              <p className="text-sm text-foreground mb-2">
                                <strong>Scenario:</strong> {scenario.scenario}
                              </p>
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                <strong>Guidance:</strong> {scenario.guidance?.substring(0, 200)}...
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                {scenario.user_name && `By ${scenario.user_name} • `}
                                {new Date(scenario.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create External Test Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create External Assessment Link</DialogTitle>
            <DialogDescription>
              Create a test link for someone who is not a CallSense user. They&apos;ll receive a unique link to complete the EEOC assessment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="external-name">Full Name</Label>
              <Input
                id="external-name"
                placeholder="Enter full name..."
                value={externalName}
                onChange={(e) => setExternalName(e.target.value)}
                data-testid="external-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="external-email">Email Address</Label>
              <Input
                id="external-email"
                type="email"
                placeholder="Enter email address..."
                value={externalEmail}
                onChange={(e) => setExternalEmail(e.target.value)}
                data-testid="external-email-input"
              />
            </div>
            
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> After creating, the test link will be automatically copied to your clipboard. 
                Share this link with the external user to complete their EEOC assessment.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCreateDialogOpen(false);
              setExternalName('');
              setExternalEmail('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTest} 
              disabled={!selectedUser || creating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Assessment Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assessment Results</DialogTitle>
            <DialogDescription>
              {selectedTest?.user_name} • {selectedTest?.user_email}
            </DialogDescription>
          </DialogHeader>
          {selectedTest && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-3xl font-bold text-foreground">{selectedTest.score || 0}%</p>
                  <p className="text-xs text-muted-foreground">Score</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  {getQualificationBadge(selectedTest.qualification_status)}
                  <p className="text-xs text-muted-foreground mt-1">Status</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-xl font-bold text-foreground">{selectedTest.attempt_count}</p>
                  <p className="text-xs text-muted-foreground">Attempts</p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={() => window.open(`/eeoc/test/${selectedTest.link_id}/results`, '_blank')}
                  variant="outline"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Full Results
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
